import "date-fns/parse"; // Importing this prevents arcane build error when MUI date pickers attempt to format dates 😖

import type { JSX } from "react";
import type { createMemoryRouter } from "react-router";
import { ApolloProvider } from "@apollo/client";
import { CharterIcon } from "@charterindex/icons";
import charterIconSvgs from "@charterindex/icons/icons.svg";
import { CssBaseline } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LicenseInfo } from "@mui/x-license";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router";
import { CrudDialog } from "./components/organisms/crud-dialog";
import { NotificationArea } from "./components/organisms/notification-area/notification-area";
import { WithPermission } from "./components/util/with-permission";
import { ErrorDialog } from "./components/organisms/error/error-dialog";
import { ErrorBoundary } from "./components/organisms/error/error-boundary";
import { MasqueradeDialog } from "./components/organisms/masquerade-dialog/masquerade-dialog";
import { PromptDialogs } from "./components/organisms/prompt-dialogs";
import { configure } from "./config";
import { PageTitleControllerProvider } from "./controllers/page-title-controller";
import { initSmartlook } from "./integrations/smartlook";
import { Services } from "./services/services";
import { ServicesProvider } from "./services/services-context";
import { Theme } from "./theme/theme-context";


CharterIcon.init(charterIconSvgs);
LicenseInfo.setLicenseKey("eb2e923c333cd6ece366f6db29329ac2Tz0xMTk3NzMsRT0xNzkwODk5MTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9MSxBVD1tdWx0aSxLVj0y");

export type RootProps = {
	router: ReturnType<typeof createBrowserRouter | typeof createMemoryRouter>;
};
const Root = (p: RootProps): JSX.Element => {
	return (
		<>
			<RouterProvider router={ p.router }/>

			<WithPermission permission="MASQUERADE">
				<MasqueradeDialog/>
			</WithPermission>

			<CrudDialog/>
			<NotificationArea/>
			<PromptDialogs/>
		</>
	);
};

window.addEventListener("load", async () => {
	const rootEl = document.getElementById("root");
	if(!rootEl) {
		throw new Error("Missing #root.");
	}
	const root = createRoot(rootEl);

	try {
		const config = configure();
		const services = await Services.initialise({
			routerFactory: createBrowserRouter,
			config,
		});

		initSmartlook(config.smartLookKey);

		root.render(
			<StrictMode>
				<ErrorBoundary services={ services }>
					<ServicesProvider services={ services }>
						<ApolloProvider client={ services.api["client"] }>
							<PageTitleControllerProvider>
								<Theme>
									<CssBaseline/>
									<LocalizationProvider dateAdapter={ AdapterDateFns }>
										<Root router={ services.navigation.router }/>
									</LocalizationProvider>
								</Theme>
							</PageTitleControllerProvider>
						</ApolloProvider>
					</ServicesProvider>
				</ErrorBoundary>
			</StrictMode>
		);
	} catch(err) {
		console.error("Failed to initialise app:", err); // eslint-disable-line no-console
		root.render(
			<Theme>
				<ErrorDialog error={ err } onClickOk={ () => window.location.reload() }/>
			</Theme>
		);
	}
});
